// AdminDashboard — the founder god-mode landing surface inside the creator.
//
// Routed off `#admin` from CreatorRoot. The server-side guard
// (admin-auth.ts) authorizes the FOUNDER_EMAIL session (with an optional
// ADMIN_EMAIL env override), so this screen is reachable the moment the
// founder signs in — no env vars required.
//
// What it shows: a live SaaS-style summary of the platform — six KPI
// tiles with sparkline trends, a focused chart of recent generations,
// a today-snapshot strip, and a recent-events feed. All numbers come
// from /v1/admin/overview, which is the same endpoint the `/admin`
// analytics shell uses; this screen is the lightweight in-creator view,
// the full multi-page shell remains a click away.
//
// Loading and error states are handled inline — a shimmering skeleton
// is shown while the fetch resolves, and a friendly empty card replaces
// the dashboard if the request fails or the DB is unbound (typical in
// a fresh dev workspace).

import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import {
  Shield,
  ExternalLink,
  KeyRound,
  Zap,
  Users,
  Sparkles,
  DollarSign,
  Activity,
  TrendingUp,
  CreditCard,
  Smartphone,
  Calendar
} from 'lucide-preact';
import { useAuth } from '@vibeshell/hooks';
import { VMetricCard, VChart, VDataGrid } from '@vibeshell/components';
import { t } from '../lib/i18n';

interface OverviewKpis {
  totalUsers: number;
  paidUsers: number;
  mrrUsd: number;
  dau: number;
  wau: number;
  mau: number;
}

interface OverviewSparklines {
  signups?: number[];
  appsGenerated?: number[];
  mrr?: number[];
  dau?: number[];
  dates?: string[];
}

interface OverviewToday {
  signups?: number;
  signins?: number;
  sessions?: number;
  appsGenerated?: number;
  generationsFree?: number;
  generationsPro?: number;
  checkoutsStarted?: number;
  payments?: number;
}

interface OverviewActivityRow {
  id?: string | number;
  event_type?: string;
  account_id?: string | null;
  device_id?: string | null;
  properties?: string | null;
  created_at?: number;
}

interface OverviewPayload {
  kpis: OverviewKpis;
  sparklines: OverviewSparklines;
  today: OverviewToday;
  activity: OverviewActivityRow[];
  note?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: OverviewPayload }
  | { kind: 'auth' }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

export function AdminDashboard(props: { onClose: () => void }): JSX.Element {
  const { session } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!session.isAdmin) return;
    let cancelled = false;
    fetch('/v1/admin/overview', { credentials: 'include' })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 401) return setState({ kind: 'auth' });
        if (res.status === 403) return setState({ kind: 'forbidden' });
        if (!res.ok) return setState({ kind: 'error', message: `HTTP ${res.status}` });
        const data = (await res.json()) as OverviewPayload;
        setState({ kind: 'ok', data });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'network'
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [session.isAdmin]);

  // Fail-closed gating preserved: non-admin sessions never see operator data.
  if (!session.isAdmin) {
    return (
      <div className="pf-page pf-has-tabbar">
        <div className="pf-topbar">
          <span className="pf-topbar__title">{t('a.admin')}</span>
        </div>
        <div className="pf-body" style={{ justifyContent: 'center', alignItems: 'center' }}><ForbiddenCard /></div>
      </div>
    );
  }

  return (
    <div className="pf-page pf-has-tabbar">
      <div className="pf-topbar">
        <span className="pf-topbar__title">{t('a.admin')}</span>
        <span className="pf-badge pf-badge--seed"><Shield size={11} /> {t('a.godMode')}</span>
        <span className="pf-topbar__spacer" />
        <a className="pf-btn pf-btn--soft pf-btn--sm" href="/admin" target="_blank" rel="noopener noreferrer">
          {t('a.openAnalytics')} <ExternalLink size={13} strokeWidth={2.5} />
        </a>
      </div>
      <div className="pf-body">
        <div style={{ maxWidth: 1080, width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Banner email={session.email ?? ''} />
          {state.kind === 'loading' && <DashboardSkeleton />}
          {state.kind === 'ok' && <DashboardBody data={state.data} />}
          <SupportTickets />
          {state.kind === 'auth' && (
            <Notice title={t('a.sessionExpired')} body={t('a.sessionExpiredBody')} />
          )}
          {state.kind === 'forbidden' && (
            <Notice title={t('a.forbidden')} body={t('a.forbiddenBody')} />
          )}
          {state.kind === 'error' && (
            <Notice title={t('a.loadErr')} body={t('a.loadErrBody', { msg: state.message })} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sections

function Banner({ email }: { email: string }): JSX.Element {
  return (
    <section
      style={{
        padding: '22px 22px 24px',
        borderRadius: 20,
        background:
          'linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 18%, var(--bg-elevated)), color-mix(in srgb, var(--color-primary) 5%, var(--bg-elevated)))',
        border: '1px solid color-mix(in srgb, var(--color-primary) 24%, var(--border))',
        boxShadow: 'var(--shadow-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span
          style={{
            display: 'grid',
            placeItems: 'center',
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'color-mix(in srgb, var(--color-primary) 22%, transparent)',
            color: 'var(--color-primary)'
          }}
          aria-hidden="true"
        >
          <Shield size={20} strokeWidth={2.25} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--color-primary)',
              margin: 0
            }}
          >
            {t('a.godMode')}
          </p>
          <p
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: 'var(--fg)',
              margin: '2px 0 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {t('a.welcome', { email: email || t('a.founder') })}
          </p>
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 10
        }}
      >
        <CapabilityChip Icon={Zap} text={t('a.cap1')} />
        {/* Founder OTP shortcut exists — the literal code is intentionally
            not surfaced in any client surface (security hygiene; the value
            lives only in worker env). Founders who need it know it. */}
        <CapabilityChip Icon={KeyRound} text={t('a.cap2')} />
      </div>
    </section>
  );
}

function DashboardBody({ data }: { data: OverviewPayload }): JSX.Element {
  const { kpis, sparklines, today, activity } = data;
  const arpu = kpis.totalUsers > 0 ? kpis.mrrUsd / kpis.totalUsers : 0;
  const proRate = kpis.totalUsers > 0 ? (kpis.paidUsers / kpis.totalUsers) * 100 : 0;
  const wauOverMau = kpis.mau > 0 ? (kpis.wau / kpis.mau) * 100 : 0;

  const generationsToday =
    (today.appsGenerated ?? 0) ||
    (today.generationsFree ?? 0) + (today.generationsPro ?? 0);

  const todayRows = [
    { label: t('a.tSignups'), value: fmt(today.signups), icon: Users },
    { label: t('a.tSignins'), value: fmt(today.signins) },
    { label: t('a.tSessions'), value: fmt(today.sessions), icon: Activity },
    { label: t('a.tApps'), value: fmt(generationsToday), icon: Sparkles },
    { label: t('a.tFree'), value: fmt(today.generationsFree) },
    { label: t('a.tPro'), value: fmt(today.generationsPro) },
    { label: t('a.tCheckouts'), value: fmt(today.checkoutsStarted), icon: CreditCard },
    { label: t('a.tSubs'), value: fmt(today.payments) }
  ];

  return (
    <>
      {/* Section: Headline KPIs */}
      <SectionHeader title={t('a.pulse')} subtitle={t('a.pulseSub')} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12
        }}
      >
        <VMetricCard
          icon={Users}
          label={t('a.totalUsers')}
          value={fmt(kpis.totalUsers)}
          delta={proRate > 0 ? t('a.proSuffix', { n: proRate.toFixed(1) }) : undefined}
          deltaTrend={proRate >= 5 ? 'up' : 'flat'}
          sparkline={sparklines.signups}
        />
        <VMetricCard
          icon={DollarSign}
          label="MRR"
          value={currency(kpis.mrrUsd)}
          delta={arpu > 0 ? t('a.arpuSuffix', { v: currency(arpu) }) : undefined}
          deltaTrend="up"
          sparkline={sparklines.mrr}
        />
        <VMetricCard
          icon={CreditCard}
          label={t('a.paidUsers')}
          value={fmt(kpis.paidUsers)}
          delta={kpis.totalUsers > 0 ? `${proRate.toFixed(1)}%` : undefined}
          deltaTrend="up"
        />
        <VMetricCard
          icon={Activity}
          label="DAU"
          value={fmt(kpis.dau)}
          sparkline={sparklines.dau}
          deltaTrend="up"
        />
        <VMetricCard
          icon={TrendingUp}
          label="WAU"
          value={fmt(kpis.wau)}
          delta={wauOverMau > 0 ? t('a.ofMau', { n: wauOverMau.toFixed(0) }) : undefined}
          deltaTrend={wauOverMau >= 40 ? 'up' : 'flat'}
        />
        <VMetricCard
          icon={Smartphone}
          label="MAU"
          value={fmt(kpis.mau)}
        />
      </div>

      {/* Section: Trend charts */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 12,
          marginTop: 6
        }}
      >
        <ChartCard
          title={t('a.appsGenerated')}
          subtitle={t('a.last30')}
          data={sparklines.appsGenerated ?? []}
          variant="area"
        />
        <ChartCard
          title={t('a.dailyActive')}
          subtitle={t('a.last30')}
          data={sparklines.dau ?? []}
          variant="line"
        />
      </div>

      {/* Section: Today + Recent activity, side-by-side on wide screens */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)',
          gap: 12,
          marginTop: 6
        }}
        class="admin-grid-stack"
      >
        <Card title={t('a.today')} subtitle={t('a.todaySub')}>
          <VDataGrid rows={todayRows} variant="comfortable" />
        </Card>
        <Card
          title={t('a.recent')}
          subtitle={t('a.recentSub')}
        >
          {activity.length === 0 ? (
            <p
              style={{
                fontSize: 13,
                color: 'var(--fg-muted)',
                margin: '8px 0',
                padding: '12px 14px',
                background: 'var(--bg-subtle)',
                borderRadius: 10
              }}
            >
              {t('a.noEvents')}
            </p>
          ) : (
            <ActivityList rows={activity.slice(0, 12)} />
          )}
        </Card>
      </div>
    </>
  );
}

function ChartCard({
  title,
  subtitle,
  data,
  variant
}: {
  title: string;
  subtitle: string;
  data: number[];
  variant: 'line' | 'area' | 'bar';
}): JSX.Element {
  return (
    <Card title={title} subtitle={subtitle}>
      <VChart data={data} variant={variant} height={140} />
    </Card>
  );
}

function ActivityList({ rows }: { rows: OverviewActivityRow[] }): JSX.Element {
  return (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4
      }}
    >
      {rows.map((row, i) => (
        <li
          key={row.id ?? `${row.event_type ?? 'event'}-${i}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            borderRadius: 10,
            background: i % 2 === 0 ? 'var(--bg-subtle)' : 'transparent'
          }}
        >
          <EventBadge type={row.event_type ?? 'event'} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <p
              style={{
                fontSize: 13,
                fontWeight: 650,
                color: 'var(--fg)',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {humanizeEvent(row.event_type ?? 'event')}
            </p>
            <p
              style={{
                fontSize: 11.5,
                color: 'var(--fg-muted)',
                margin: '2px 0 0',
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {row.account_id
                ? `acct ${row.account_id.slice(0, 8)}…`
                : row.device_id
                ? `device ${row.device_id.slice(0, 8)}…`
                : t('a.anon')}
            </p>
          </div>
          <span
            style={{
              fontSize: 11.5,
              color: 'var(--fg-subtle)',
              whiteSpace: 'nowrap'
            }}
          >
            {relTime(row.created_at)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function EventBadge({ type }: { type: string }): JSX.Element {
  const tone = eventTone(type);
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'grid',
        placeItems: 'center',
        width: 28,
        height: 28,
        borderRadius: 8,
        background: `color-mix(in srgb, ${tone} 14%, var(--bg-elevated))`,
        color: tone,
        flexShrink: 0
      }}
    >
      <Calendar size={13} strokeWidth={2.5} />
    </span>
  );
}

function CapabilityChip({
  Icon,
  text
}: {
  Icon: typeof Shield;
  text: string;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '8px 12px',
        borderRadius: 10,
        background: 'color-mix(in srgb, var(--color-primary) 9%, var(--bg-elevated))',
        border: '1px solid color-mix(in srgb, var(--color-primary) 14%, var(--border))'
      }}
    >
      <Icon size={14} strokeWidth={2.25} color="var(--color-primary)" />
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg)' }}>{text}</span>
    </div>
  );
}

function SectionHeader({
  title,
  subtitle
}: {
  title: string;
  subtitle: string;
}): JSX.Element {
  return (
    <div style={{ marginTop: 4, marginBottom: -4 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg)', margin: 0 }}>
        {title}
      </h2>
      <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '3px 0 0' }}>{subtitle}</p>
    </div>
  );
}

function Card({
  title,
  subtitle,
  children
}: {
  title: string;
  subtitle?: string;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  return (
    <section
      style={{
        padding: 18,
        borderRadius: 16,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minWidth: 0
      }}
    >
      <header>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)', margin: 0 }}>{title}</h3>
        {subtitle && (
          <p style={{ fontSize: 12, color: 'var(--fg-muted)', margin: '2px 0 0' }}>{subtitle}</p>
        )}
      </header>
      {children}
    </section>
  );
}

function Notice({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <section
      style={{
        padding: '18px 20px',
        borderRadius: 14,
        background: 'var(--bg-elevated)',
        border: '1px dashed var(--border)',
        textAlign: 'center'
      }}
    >
      <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)', margin: 0 }}>{title}</p>
      <p
        style={{
          fontSize: 13,
          color: 'var(--fg-muted)',
          margin: '6px 0 0',
          lineHeight: 1.55,
          maxWidth: 480,
          marginInline: 'auto'
        }}
      >
        {body}
      </p>
    </section>
  );
}

function DashboardSkeleton(): JSX.Element {
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonTile key={i} height={120} />
        ))}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 12
        }}
      >
        <SkeletonTile height={200} />
        <SkeletonTile height={200} />
      </div>
    </>
  );
}

function SkeletonTile({ height }: { height: number }): JSX.Element {
  return (
    <div
      class="skeleton-shimmer"
      style={{
        height,
        borderRadius: 16,
        border: '1px solid var(--border)'
      }}
      aria-hidden="true"
    />
  );
}

function ForbiddenCard(): JSX.Element {
  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '40px 24px' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          padding: '48px 24px',
          borderRadius: 18,
          border: '1px solid var(--border)',
          background: 'var(--bg-elevated)',
          textAlign: 'center'
        }}
      >
        <span
          style={{
            display: 'grid',
            placeItems: 'center',
            width: 56,
            height: 56,
            borderRadius: 14,
            background: 'var(--bg-subtle)',
            color: 'var(--fg-subtle)'
          }}
          aria-hidden="true"
        >
          <Shield size={24} />
        </span>
        <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg)', margin: 0 }}>
          {t('a.founderOnly')}
        </p>
        <p
          style={{
            fontSize: 13.5,
            lineHeight: 1.55,
            color: 'var(--fg-muted)',
            margin: 0,
            maxWidth: 320
          }}
        >
          {t('a.founderOnlyBody')}
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Formatting helpers

function fmt(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString('en-US');
}

function currency(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 10_000) return `$${(n / 1000).toFixed(1)}k`;
  if (n >= 1_000) return `$${(n / 1000).toFixed(2)}k`;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: n < 10 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function relTime(sec: number | undefined): string {
  if (!sec) return '—';
  const diffMs = Date.now() - sec * 1000;
  if (diffMs < 0) return t('a.now');
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return t('a.secAgo', { n: s });
  const m = Math.floor(s / 60);
  if (m < 60) return t('a.minAgo', { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('a.hrAgo', { n: h });
  const d = Math.floor(h / 24);
  if (d < 7) return t('a.dayAgo', { n: d });
  return t('a.wkAgo', { n: Math.floor(d / 7) });
}

// Event type → i18n key. Resolved via t() at call time so the active locale wins.
const EVENT_KEYS: Record<string, string> = {
  signup: 'a.evSignup',
  signin: 'a.evSignin',
  signout: 'a.evSignout',
  session_started: 'a.evSession',
  app_generated: 'a.evAppGen',
  app_revised: 'a.evAppRev',
  app_exported: 'a.evAppExp',
  app_cloned: 'a.evAppClone',
  app_installed: 'a.evAppInstall',
  checkout_started: 'a.evCheckout',
  subscription_started: 'a.evSubStart',
  subscription_upgraded: 'a.evSubUp',
  subscription_cancelled: 'a.evSubCancel',
  credits_consumed: 'a.evCredits'
};

function humanizeEvent(type: string): string {
  const key = EVENT_KEYS[type];
  if (key) return t(key);
  return type.replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase());
}

function eventTone(type: string): string {
  if (type === 'signup' || type === 'subscription_started' || type === 'subscription_upgraded')
    return 'rgb(5, 150, 105)';
  if (type === 'subscription_cancelled' || type === 'signout') return 'rgb(225, 29, 72)';
  if (type === 'app_generated' || type === 'app_revised') return 'var(--color-primary)';
  return 'rgb(115, 115, 115)';
}

// ─── Support tickets (built-in support form submissions) ────────────────────
interface SupportTicketRow {
  id: string;
  account_id: string | null;
  email: string;
  topic: string;
  subject: string;
  message: string;
  status: string;
  created_at: number;
  media?: Array<{ id: string; type: string; bytes: number; name: string | null }>;
}

function SupportTickets(): JSX.Element {
  const [tickets, setTickets] = useState<SupportTicketRow[] | null>(null);
  const [openCount, setOpenCount] = useState(0);
  const [err, setErr] = useState('');

  function load(): void {
    fetch('/v1/admin/support-tickets', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          setErr(`HTTP ${res.status}`);
          return;
        }
        const d = (await res.json()) as { tickets: SupportTicketRow[]; openCount: number };
        setTickets(d.tickets || []);
        setOpenCount(d.openCount || 0);
        setErr('');
      })
      .catch(() => setErr('network'));
  }

  useEffect(() => {
    load();
  }, []);

  async function setStatus(id: string, status: string): Promise<void> {
    await fetch(`/v1/admin/support-tickets/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ status })
    }).catch(() => {});
    load();
  }

  return (
    <section>
      <SectionHeader title={t('a.tickets')} subtitle={tickets ? t('a.ticketsSub', { open: openCount, total: tickets.length }) : t('a.loading')} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {err && <p style={{ color: 'var(--fg-muted)', fontSize: 13, margin: 0 }}>{t('a.ticketsErr', { err })}</p>}
        {tickets && tickets.length === 0 && <p style={{ color: 'var(--fg-muted)', fontSize: 13, margin: 0 }}>{t('a.noTickets')}</p>}
        {tickets &&
          tickets.map((tk) => {
            const isOpen = tk.status === 'open';
            return (
              <div key={tk.id} style={{ border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-elevated)', padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                  <strong style={{ color: 'var(--fg)', fontSize: 14 }}>{tk.subject}</strong>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 999,
                      whiteSpace: 'nowrap',
                      background: isOpen ? 'rgba(220,38,38,0.12)' : 'var(--bg-subtle)',
                      color: isOpen ? '#DC2626' : 'var(--fg-muted)'
                    }}
                  >
                    {ticketStatusLabel(tk.status)}
                  </span>
                </div>
                <p style={{ margin: '4px 0', fontSize: 12, color: 'var(--fg-muted)' }}>
                  {tk.email} · {tk.topic}
                </p>
                <p style={{ margin: '6px 0 10px', fontSize: 13, color: 'var(--fg)', whiteSpace: 'pre-wrap' }}>{tk.message}</p>
                {tk.media && tk.media.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '0 0 10px' }}>
                    {tk.media.map((m) => {
                      const url = `/v1/admin/support-tickets/${tk.id}/media/${m.id}`;
                      return m.type.startsWith('video/') ? (
                        <video key={m.id} src={url} controls preload="metadata" style={{ maxWidth: 220, maxHeight: 160, borderRadius: 8, border: '1px solid var(--border)', background: '#000' }} />
                      ) : (
                        <a key={m.id} href={url} target="_blank" rel="noreferrer" title={m.name ?? 'attachment'}>
                          <img src={url} alt={m.name ?? 'attachment'} style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', display: 'block' }} />
                        </a>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['reviewed', 'resolved', 'dismissed'].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatus(tk.id, s)}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '5px 10px',
                        borderRadius: 8,
                        border: '1px solid var(--border-strong)',
                        background: tk.status === s ? 'var(--fg)' : 'transparent',
                        color: tk.status === s ? 'var(--bg-elevated)' : 'var(--fg)',
                        cursor: 'pointer'
                      }}
                    >
                      {ticketStatusLabel(s)}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    </section>
  );
}

// Ticket status enum → localized label.
function ticketStatusLabel(status: string): string {
  const map: Record<string, string> = {
    open: 'a.stOpen',
    reviewed: 'a.stReviewed',
    resolved: 'a.stResolved',
    dismissed: 'a.stDismissed'
  };
  return map[status] ? t(map[status]) : status;
}
